import { MINIMUM_METADATA_INTERVAL, MINIMUM_SNMP_INTERVAL } from '@kentik/ui-shared/nms/deviceMonitoringTemplates';

const numericTransform = (value) => {
  const numericValue = parseInt(value, 10);

  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    return 0;
  }

  return numericValue;
};

// These defaults are used by ranger when no polling config exists in the template.
export const createPollingSnmpDefaults = () => ({
  Retries: {
    defaultValue: 2,
    label: 'Retries',
    helpText: 'Number of times to retry connecting',
    type: 'number',
    transform: {
      in: numericTransform,
      out: numericTransform
    }
  },
  Timeout: {
    defaultValue: '2.5s',
    label: 'Timeout',
    helpText: 'Wait time before a timeout declaration'
  },
  MaxOids: {
    defaultValue: 50,
    label: 'Max SNMP OIDs per Request',
    helpText: 'Number of SNMP OIDs per single SNMP GET',
    type: 'number',
    transform: {
      in: numericTransform,
      out: numericTransform
    }
  },
  MaxRepetitions: {
    defaultValue: 50,
    label: 'Max Repetitions (for GETBULK)',
    helpText: 'Number of OIDs in each SNMP bulk-walk query',
    type: 'number',
    transform: {
      in: numericTransform,
      out: numericTransform
    }
  },
  Workers: {
    defaultValue: 2,
    label: 'Simultaneous Polls Supported',
    helpText: 'Max number of parallel SNMP sessions',
    type: 'number',
    transform: {
      in: numericTransform,
      out: numericTransform
    }
  }
});

export const createMonitoringTemplateFields = ({
  runOnlyListedReportsField,
  optModeField,
  userEditablePollingSnmpFieldConfigs,
  defaultPollingSnmpFieldConfigs = userEditablePollingSnmpFieldConfigs
}) => ({
  name: { label: 'Name', rules: ['required'], messages: { not_in: 'This name has already been used' } },
  description: { label: 'Description' },
  'settings.intervals.fastMetrics': {
    label: 'Fast Metrics',
    rules: ['required', 'validDuration', `minDuration:${MINIMUM_SNMP_INTERVAL}`],
    helpText: 'CPU, memory, interface statistics'
  },
  'settings.intervals.slowMetrics': {
    label: 'Slow Metrics',
    rules: ['required', 'validDuration', `minDuration:${MINIMUM_SNMP_INTERVAL}`],
    helpText: 'BGP, IS-IS, hardware'
  },
  'settings.intervals.metadata': {
    label: 'Metadata',
    rules: ['required', 'validDuration', `minDuration:${MINIMUM_METADATA_INTERVAL}`],
    helpText: 'Model, manufacturer, etc.'
  },
  'settings.run-only-listed-reports': runOnlyListedReportsField,
  'settings.flow-mode': {
    options: [
      { label: 'Basic', value: 'standard' },
      { label: 'Minimum', value: 'minimum' }
    ],
    defaultValue: 'standard'
  },
  'uiProperties.polling.snmp.useDefaults': {
    defaultValue: true
  },
  'uiProperties.measurements.subscriptions.everything': {
    defaultValue: false
  },
  'uiProperties.measurements.subscriptions.essentials': {
    defaultValue: false
  },
  'uiProperties.measurements.subscriptions.customSnmp': {
    defaultValue: false
  },
  // Keep user-entered polling values so they can be restored when leaving defaults mode.
  'uiProperties.polling.snmp.retries': defaultPollingSnmpFieldConfigs.Retries,
  'uiProperties.polling.snmp.timeout': defaultPollingSnmpFieldConfigs.Timeout,
  'uiProperties.polling.snmp.max-oids': defaultPollingSnmpFieldConfigs.MaxOids,
  'uiProperties.polling.snmp.max-repetitions': defaultPollingSnmpFieldConfigs.MaxRepetitions,
  'uiProperties.polling.snmp.workers': defaultPollingSnmpFieldConfigs.Workers,
  'settings.polling.snmp.retries': userEditablePollingSnmpFieldConfigs.Retries,
  'settings.polling.snmp.timeout': userEditablePollingSnmpFieldConfigs.Timeout,
  'settings.polling.snmp.max-oids': userEditablePollingSnmpFieldConfigs.MaxOids,
  'settings.polling.snmp.max-repetitions': userEditablePollingSnmpFieldConfigs.MaxRepetitions,
  'settings.polling.snmp.workers': userEditablePollingSnmpFieldConfigs.Workers,
  'simple.measurements.system': {
    label: 'System',
    options: [
      { label: 'CPU', value: '/system/cpus' },
      { label: 'Memory', value: '/system/memory' },
      { label: 'Temperature', value: '/components/temperature' }
    ]
  },
  'measurement.optMode': optModeField,
  'simple.measurements.interfaces': {
    label: 'Interfaces',
    options: [
      { label: 'Interface counters', value: '/interfaces/counters' },
      { label: 'Transceiver/optic metrics', value: '/interfaces/transceiver' }
    ]
  },
  'simple.measurements.hardware': {
    label: 'Hardware',
    options: [
      { label: 'Power supplies', value: '/components/power-supply' },
      { label: 'Port sensors', value: '/components/sensor' },
      { label: 'Fans', value: '/components/fan' },
      { label: 'Other', value: '/components' }
    ]
  },
  'simple.measurements.protocols': {
    label: 'Routing Protocols',
    options: [
      { label: 'BGP', value: '/protocols/bgp/*' },
      { label: 'IS-IS', value: '/protocols/isis/*' }
    ]
  },
  _interfaceGroup: {
    defaultValue: undefined
  }
});
